import { useEffect, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { useRuntimeConfig } from '@/config/useRuntimeConfig';
import { useAuth } from '@/auth/useAuth';
import { normalizeApiErrorDetails, retireApp, type AdminRegistryApp } from '@/api/console';
import { OrgAdminApiError } from '@/api/orgAdmin';
import { Button, Callout, Modal, useToast } from '@/components/ui';

export interface RetireAppModalProps {
    /** The app pending retirement — `null` closes the modal. */
    app: AdminRegistryApp | null;
    onClose: () => void;
}

/**
 * EAA-1884 (closes the EAA-1817 plan's gap #1, "Việc cần cân nhắc" card):
 * `DELETE /admin/apps/{code}` is a SOFT deregister — the row is retained,
 * `lifecycle` moves to `retired`, and its Keycloak client is disabled. The
 * server refuses a first attempt when live `RoleMap`/`Assignment`/
 * `OrganizationAppScope` rows still reference the app, naming each dependency
 * type + count in `error.details.dependencies`; this modal shows that list
 * and only then offers a `force=true` retry — never forcing on the first
 * click, per this ticket's own instruction.
 *
 * Post-success navigation: deliberately STAYS on `/console/apps/:code`
 * rather than routing back to `/console/apps`. The registry row is retained
 * (soft deregister, not a hard delete), so the Roles/Connection/Audit tabs
 * remain valid, read-only history for a retired app — useful for exactly the
 * audit trail this action creates. The only thing that needs to change is
 * this card disappearing, which the `lifecycle==='retired'` guard in
 * `AppDetailConsiderationsCard` already does once the invalidated `admin-apps`
 * query refetches.
 */
export function RetireAppModal({ app, onClose }: RetireAppModalProps) {
    const { t } = useTranslation();
    const config = useRuntimeConfig();
    const { accessToken } = useAuth();
    const queryClient = useQueryClient();
    const toast = useToast();
    // EAA-1884: null until the first attempt 400s with live dependencies. Once
    // populated, the confirm button becomes the force retry — a SEPARATE,
    // explicit second click, not an automatic retry.
    const [dependencies, setDependencies] = useState<string[] | null>(null);

    useEffect(() => {
        if (app === null) setDependencies(null);
    }, [app]);

    const retireMutation = useMutation({
        mutationFn: (vars: { target: AdminRegistryApp; force: boolean }) =>
            retireApp(config.apiBase, accessToken as string, vars.target.code, {
                force: vars.force,
            }),
        onSuccess: (_data, vars) => {
            toast.success(
                t('console.appDetail.overview.considerations.retireModal.toastSuccess', {
                    name: vars.target.name,
                })
            );
            handleClose();
            void queryClient.invalidateQueries({ queryKey: ['admin-apps'] });
            void queryClient.invalidateQueries({ queryKey: ['apps-registry'] });
            void queryClient.invalidateQueries({ queryKey: ['role-map-sync-status'] });
        },
        onError: (error: unknown, vars) => {
            if (!vars.force && error instanceof OrgAdminApiError) {
                const details = normalizeApiErrorDetails(error.details);
                if (details.dependencies?.length) {
                    setDependencies(details.dependencies);
                    return;
                }
            }
            const message = error instanceof Error ? error.message : undefined;
            toast.error(
                message || t('console.appDetail.overview.considerations.retireModal.toastError')
            );
        },
    });

    function handleClose() {
        if (retireMutation.isPending) return;
        setDependencies(null);
        onClose();
    }

    const isForceStep = dependencies !== null;

    return (
        <Modal
            open={app !== null}
            onCancel={handleClose}
            title={t('console.appDetail.overview.considerations.retireModal.title')}
            footer={
                <>
                    <Button onClick={handleClose} disabled={retireMutation.isPending}>
                        {t('common.cancel')}
                    </Button>
                    <Button
                        danger
                        type="primary"
                        onClick={() => app && retireMutation.mutate({ target: app, force: isForceStep })}
                        disabled={retireMutation.isPending}
                    >
                        {retireMutation.isPending
                            ? t('console.appDetail.overview.considerations.retireModal.pending')
                            : isForceStep
                              ? t('console.appDetail.overview.considerations.retireModal.forceButton')
                              : t('console.appDetail.overview.considerations.retireModal.confirmButton')}
                    </Button>
                </>
            }
        >
            {app && (
                <>
                    <p>
                        {t('console.appDetail.overview.considerations.retireModal.body', {
                            name: app.name,
                            code: app.code,
                        })}
                    </p>
                    {isForceStep && (
                        <Callout
                            tone="danger"
                            label={t(
                                'console.appDetail.overview.considerations.retireModal.dependenciesLabel'
                            )}
                            longLabel
                        >
                            <ul className="app-detail-dependency-list">
                                {dependencies?.map((line) => <li key={line}>{line}</li>)}
                            </ul>
                        </Callout>
                    )}
                </>
            )}
        </Modal>
    );
}

export default RetireAppModal;
